import type { ZodTypeAny } from 'zod';
import type
{
    AgentTools,
    AgentToolsCallResult,
    AgentToolsHandler,
    AgentToolsListResult,
    AgentToolsRegistrationLevel,
    AgentToolsRegistrationOptions,
    AgentToolsSessionReader,
    AgentToolsToolRegistrationOptions
} from '#layers/core/app/runtime/types/tools';

interface ToolRegistration
{
    kind: 'tool';
    description: string;
    handler: AgentToolsHandler;
    schema: ZodTypeAny;
    argumentsDescription: string;
}

interface TextRegistration
{
    kind: 'prompt' | 'resource';
    description: string;
    handler: AgentToolsHandler;
}

// Rozłączna suma zamiast jednego typu z opcjonalnym `schema` - dzięki temu w call() nie ma gałęzi
// "narzędzie bez schematu", która po cichu przepuściłaby wywołanie bez sprawdzenia argumentów
type Registration = ToolRegistration | TextRegistration;

// Kanał, którym handler zgłasza call() niepowodzenie domenowe (np. goto.* - "brak uprawnień",
// "nazwa nie istnieje"), odróżnialne od nieoczekiwanego wyjątku (`handler-error`). Wyjątek typowany
// jest jednoznaczny - w przeciwieństwie do rozpoznawania zwykłego obiektu po polu `ok` nie myli
// odmowy z danymi, w których `ok` mogłoby być częścią treści zwracanej przez zasób
//
// Wolno go rzucać tylko w kodzie należącym do narzędzia, które ten powód zgłasza - w handlerze albo
// w jego prywatnym helperze, wołanym z jednego miejsca. Nie wolno go rzucać w kodzie współdzielonym
// (store, serwis, wspólne narzędzie) - `catch` w call() obejmuje cały stos wywołań handlera, więc
// wołający dostałby odmowę z cudzego powodu i nie odróżniłby jej od świadomej
export class ToolCallFailure extends Error
{
    public readonly reason: string;
    public readonly data?: unknown;

    public constructor(reason: string, message: string, data?: unknown)
    {
        super(message);
        this.reason = reason;
        this.data = data;
    }
}

// Przestrzeń nazw jest segmentem przed pierwszą kropką, a nazwą właściwą cała reszta - dlatego
// `roles.form` i `roles.form.set` należą do tej samej przestrzeni `roles`. Rejestracja bez kropki
// nie ma przestrzeni, więc gwarancja "kolizja tylko w tej samej przestrzeni" nie miałaby czego pilnować
function namespaceOf(name: string): string
{
    const separator = name.indexOf('.');

    if (separator <= 0 || separator === name.length - 1)
    {
        throw new Error(`Nazwa rejestracji musi mieć postać "przestrzen.nazwa" (otrzymano "${name}").`);
    }

    return name.slice(0, separator);
}

// Rdzeń rejestru narzędzi agenta. Zwykła klasa z odczytem sesji wstrzykniętym jako funkcja - bez
// zależności od useNuxtApp/Pinia da się utworzyć i przetestować w projekcie testowym `unit`
// (environment: node, bez Nuxt). Plugin (`plugins/tools.ts`) tylko ją tworzy i wiesza na window.edito
export class AgentToolsRegistry implements AgentTools
{
    private sessionReader: AgentToolsSessionReader;
    private global = new Map<string, Registration>();
    private ephemeral = new Map<string, Registration>();

    public constructor(sessionReader: AgentToolsSessionReader)
    {
        this.sessionReader = sessionReader;
    }

    private register(name: string, registration: Registration, level: AgentToolsRegistrationLevel): void
    {
        const namespace = namespaceOf(name);

        // Kolizja liczy się na pełnej nazwie w obu poziomach naraz, nie tylko w poziomie docelowym -
        // inaczej call() nie miałby jak jednoznacznie rozstrzygnąć, którą rejestrację wywołać
        if (this.global.has(name) || this.ephemeral.has(name))
        {
            throw new Error(`Nazwa "${name}" jest już zarejestrowana w przestrzeni "${namespace}".`);
        }

        (level === 'global' ? this.global : this.ephemeral).set(name, registration);
    }

    public registerTool(name: string, description: string, schema: ZodTypeAny, handler: AgentToolsHandler, options: AgentToolsToolRegistrationOptions): void
    {
        // Wymóg schematu musi obowiązywać w czasie działania, bo rejestr wisi na window i rejestruje
        // się do niego kod, którego typy nikt nie sprawdza - sam typ TypeScript to gwarancja kompilacji
        if (schema == null || typeof schema.safeParse !== 'function')
        {
            throw new Error(`Narzędzie "${name}" wymaga schematu argumentów z metodą safeParse.`);
        }

        this.register(name, { kind: 'tool', description, handler, schema, argumentsDescription: options.argumentsDescription }, options.level);
    }

    public registerPrompt(name: string, description: string, handler: AgentToolsHandler, options: AgentToolsRegistrationOptions): void
    {
        this.register(name, { kind: 'prompt', description, handler }, options.level);
    }

    public registerResource(name: string, description: string, handler: AgentToolsHandler, options: AgentToolsRegistrationOptions): void
    {
        this.register(name, { kind: 'resource', description, handler }, options.level);
    }

    // Woła to plugin: przy zmianie trasy i przy przejściu sesji z zalogowanej na niezalogowaną. Metoda
    // jest publiczna, bo plugin jest osobnym plikiem, ale nie należy do kontraktu `AgentTools` - i na
    // window.edito.tools plugin wiesza obiekt zawężony do pięciu metod kontraktu, nie tę instancję
    public clearEphemeral(): void
    {
        this.ephemeral.clear();
    }

    public list(): AgentToolsListResult
    {
        const session = this.sessionReader();
        const result: AgentToolsListResult = { tools: [], prompts: [], resources: [] };

        if (session.loaded !== true || session.authenticated !== true)
        {
            return result;
        }

        for (const registrations of [this.global, this.ephemeral])
        {
            for (const [name, registration] of registrations)
            {
                if (registration.kind === 'tool')
                {
                    result.tools.push({ name, description: registration.description, argumentsDescription: registration.argumentsDescription });
                }
                else if (registration.kind === 'prompt')
                {
                    result.prompts.push({ name, description: registration.description });
                }
                else
                {
                    result.resources.push({ name, description: registration.description });
                }
            }
        }

        return result;
    }

    public async call(name: string, args?: unknown): Promise<AgentToolsCallResult>
    {
        const session = this.sessionReader();

        if (session.loaded !== true)
        {
            return { ok: false, reason: 'session-unknown', message: 'Stan sesji nie został jeszcze ustalony.' };
        }

        if (session.authenticated !== true)
        {
            return { ok: false, reason: 'unauthenticated', message: 'Brak zalogowanej sesji.' };
        }

        const registration = this.global.get(name) ?? this.ephemeral.get(name);

        if (!registration)
        {
            return { ok: false, reason: 'not-found', message: `Nie znaleziono rejestracji "${name}".` };
        }

        // Schemat argumentów wymagany jest tylko dla narzędzi (registerTool) - prompt i zasób nie
        // dostają argumentów wołającego wcale, więc payload zostaje `undefined` niezależnie od `args`
        let payload: unknown;

        if (registration.kind === 'tool')
        {
            const parsed = registration.schema.safeParse(args);

            if (!parsed.success)
            {
                return { ok: false, reason: 'invalid-arguments', message: parsed.error.message };
            }

            // Handler dostaje wartość po walidacji, nie surowe argumenty - inaczej z.coerce, .default
            // i .transform ze schematu nie miałyby żadnego skutku, a nadmiarowe klucze, które zwykły
            // z.object usuwa przy parsowaniu, trafiałyby do handlera
            payload = parsed.data;
        }

        try
        {
            const data = await registration.handler(payload);

            return { ok: true, data };
        }
        catch (error)
        {
            if (error instanceof ToolCallFailure)
            {
                return { ok: false, reason: error.reason, message: error.message, data: error.data };
            }

            return { ok: false, reason: 'handler-error', message: error instanceof Error ? error.message : String(error) };
        }
    }
}
